import React, { useEffect, useState, useMemo } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { FilterBar } from '@/components/ui/FilterBar';
import { api } from '@/api/client';
import { exportToCSV, CSV_COLUMNS } from '@/utils/exportUtils';
import { Plus, Edit } from 'lucide-react';

export const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', address: '', nic: '', is_dealer: false, credit_limit: 0, default_credit_days: 30 });

  useEffect(() => {
    loadCustomers();
  }, []);

  const filtered = useMemo(() => {
    if (!searchTerm) return customers;
    const q = searchTerm.toLowerCase();
    return customers.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.phone.includes(searchTerm) ||
      c.email?.toLowerCase().includes(q) ||
      c.nic?.toLowerCase().includes(q)
    );
  }, [customers, searchTerm]);

  const loadCustomers = async () => {
    try {
      const res = await api.getCustomers();
      setCustomers(res.data);
    } catch (error) { console.error(error); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.updateCustomer(editing.id, formData);
      } else {
        await api.createCustomer(formData);
      }
      loadCustomers();
      setShowModal(false);
      setEditing(null);
    } catch (error) { alert('Failed to save customer'); }
  };

  const openModal = (customer = null) => {
    if (customer) {
      setEditing(customer);
      setFormData(customer);
    } else {
      setEditing(null);
      setFormData({ name: '', phone: '', email: '', address: '', nic: '', is_dealer: false, credit_limit: 0, default_credit_days: 30 });
    }
    setShowModal(true);
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Customers</h1>
        <Button onClick={() => openModal()} data-testid="add-customer-button">
          <Plus size={20} className="mr-2" />
          Add Customer
        </Button>
      </div>

      <Card>
        <FilterBar
          search={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search by name, phone, email, or NIC..."
          onClear={() => setSearchTerm('')}
          onExport={() => exportToCSV(filtered, 'customers', CSV_COLUMNS.customers)}
          testId="customers-filter"
        />
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>NIC</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-gray-500">No customers found</TableCell>
              </TableRow>
            ) : (
              filtered.map((customer) => (
                <TableRow key={customer.id}>
                  <TableCell className="font-medium">{customer.name}</TableCell>
                  <TableCell>{customer.phone}</TableCell>
                  <TableCell>{customer.email || '-'}</TableCell>
                  <TableCell>{customer.nic || '-'}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" onClick={() => openModal(customer)} data-testid={`edit-customer-${customer.id}`}>
                      <Edit size={16} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editing ? 'Edit Customer' : 'Add Customer'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input label="Name *" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
          <Input label="Phone *" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required />
          <Input label="Email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
          <Input label="Address" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
          <Input label="NIC" value={formData.nic} onChange={(e) => setFormData({ ...formData, nic: e.target.value })} />
          <div className="border-t border-[var(--border)] pt-3 mt-2">
            <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold mb-3">
              <input
                type="checkbox"
                checked={!!formData.is_dealer}
                onChange={(e) => setFormData({ ...formData, is_dealer: e.target.checked })}
                className="w-4 h-4"
                data-testid="customer-is-dealer"
              />
              <span>Mark as Dealer (eligible for credit sales)</span>
            </label>
            {formData.is_dealer && (
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Credit Limit (LKR)"
                  type="number"
                  step="0.01"
                  value={formData.credit_limit || 0}
                  onChange={(e) => setFormData({ ...formData, credit_limit: parseFloat(e.target.value) || 0 })}
                  data-testid="customer-credit-limit"
                />
                <div>
                  <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Default Credit Days</label>
                  <select
                    value={formData.default_credit_days || 30}
                    onChange={(e) => setFormData({ ...formData, default_credit_days: parseInt(e.target.value) || 30 })}
                    className="w-full px-3 py-2 rounded-md bg-[var(--surface)] border border-[var(--border)] text-sm"
                    data-testid="customer-credit-days"
                  >
                    <option value={7}>7 Days</option>
                    <option value={14}>14 Days</option>
                    <option value={21}>21 Days</option>
                    <option value={30}>30 Days</option>
                  </select>
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit">Save</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
